/**
 * Types for Unified Registration System
 * أنواع البيانات لنظام التسجيل الموحد
 */

// نوع المستخدم
export type UserType = 'student' | 'parent';

// بيانات التسجيل الأساسية
export interface BaseRegistrationData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  userType: UserType;
  acceptTerms: boolean;
}

// بيانات تسجيل الطالب
export interface StudentRegistrationData extends BaseRegistrationData {
  userType: 'student';
  grade: string;
  dateOfBirth?: Date;
  parentCode?: string; // كود ولي الأمر (اختياري)
}

// بيانات تسجيل ولي الأمر
export interface ParentRegistrationData extends BaseRegistrationData {
  userType: 'parent';
  studentCode: string; // كود الطالب (مطلوب)
  occupation?: string;
  address?: string;
}

// نوع بيانات التسجيل الموحد
export type UnifiedRegistrationData =
  | StudentRegistrationData
  | ParentRegistrationData;

// حالات الخطوات
export interface RegistrationStep {
  step: number;
  title: string;
  description: string;
  isCompleted: boolean;
  isActive: boolean;
}

// نتيجة التسجيل
export interface RegistrationResult {
  success: boolean;
  accountCreated?: boolean;
  codeGenerated?: boolean;
  linkPendingApproval?: boolean;
  linkRequestCreated?: boolean;
  userId?: string;
  userType?: UserType;
  linkedUserId?: string; // معرف المستخدم المرتبط (ولي الأمر أو الطالب)
  generatedCode?: string; // الكود المُنشأ للمستخدم الجديد
  error?: string;
  warnings?: string[];
}

// حالة الربط
export interface LinkingStatus {
  isLinked: boolean;
  linkedWith?: {
    id: string;
    name: string;
    email: string;
    type: UserType;
  };
  hasCode?: boolean;
  code?: string;
  codeExpiresAt?: Date;
}

// إعدادات التسجيل
export interface RegistrationSettings {
  allowStudentRegistration: boolean;
  allowParentRegistration: boolean;
  requireParentCode: boolean; // هل كود ولي الأمر مطلوب للطالب؟
  requireStudentCode: boolean; // هل كود الطالب مطلوب لولي الأمر؟
  autoLinkWithTeacher: boolean; // ربط تلقائي مع المدرس الرئيسي
  codeExpirationDays: number; // مدة انتهاء صلاحية الكود
  minPasswordLength: number;
  requirePhoneVerification: boolean;
  requireEmailVerification: boolean;
}

// قائمة الصفوف الدراسية
export const GRADES = [
  'KG1 - روضة أولى',
  'KG2 - روضة ثانية',
  'الأول الابتدائي',
  'الثاني الابتدائي',
  'الثالث الابتدائي',
  'الرابع الابتدائي',
  'الخامس الابتدائي',
  'السادس الابتدائي',
  'الأول المتوسط',
  'الثاني المتوسط',
  'الثالث المتوسط',
  'الأول الثانوي',
  'الثاني الثانوي',
  'الثالث الثانوي',
];

// أشكال التحقق من صحة البيانات
export interface ValidationErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  phone?: string;
  grade?: string;
  parentCode?: string;
  studentCode?: string;
  occupation?: string;
  address?: string;
  acceptTerms?: string;
}

// رسائل الأخطاء
export const ERROR_MESSAGES = {
  required: 'هذا الحقل مطلوب',
  invalidEmail: 'البريد الإلكتروني غير صحيح',
  passwordTooShort: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
  passwordMismatch: 'كلمة المرور غير متطابقة',
  invalidPhone: 'رقم الهاتف غير صحيح',
  invalidCode: 'الكود غير صحيح أو منتهي الصلاحية',
  invalidStudentCode: 'كود الطالب يجب أن يكون بصيغة S-XXXXXXXX',
  invalidParentCode: 'كود ولي الأمر يجب أن يكون بصيغة P-XXXXXXXX',
  codeRequired: 'الكود مطلوب',
  termsRequired: 'يجب الموافقة على شروط الاستخدام وسياسة الخصوصية',
  emailExists: 'البريد الإلكتروني مُستخدم بالفعل',
  networkError: 'خطأ في الاتصال بالإنترنت',
  unknownError: 'حدث خطأ غير متوقع',
};

export const STUDENT_CODE_PATTERN = /^S-[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{8}$/;
export const PARENT_CODE_PATTERN = /^P-[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{8}$/;

// أنواع الرسائل
export type MessageType = 'success' | 'error' | 'warning' | 'info';

// رسالة النظام
export interface SystemMessage {
  type: MessageType;
  title: string;
  message: string;
  duration?: number; // بالثواني
  action?: {
    label: string;
    onClick: () => void;
  };
}

// No default export needed - all exports are named
